import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { api, imgSrc } from "../lib/api";
import { useMeta } from "../hooks/useMeta";
import { FadeUp, LineReveal, Eyebrow } from "../components/Reveal";
import { CtaSection } from "../components/CtaSection";
import { ShareButtons } from "../components/ShareButtons";

export default function ArtikelDetail() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useMeta({
    title: article?.judul,
    description: article?.ringkasan,
    image: article ? imgSrc(article.gambar) : undefined,
    type: "article",
  });

  useEffect(() => {
    setArticle(null);
    setNotFound(false);
    api
      .get(`/articles/${slug}`)
      .then((r) => setArticle(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <section className="flex min-h-screen flex-col items-center justify-center px-6 text-center">
        <h1
          data-testid="article-not-found"
          className="font-serif text-5xl italic text-[#F5F5F5]"
        >
          Artikel tidak ditemukan.
        </h1>
        <Link
          to="/artikel"
          data-testid="article-back-button"
          className="mt-8 inline-flex items-center gap-3 border border-gold/50 px-6 py-3 text-[12px] font-semibold uppercase tracking-[0.2em] text-gold transition-all hover:bg-gold hover:text-ink"
        >
          <ArrowLeft size={15} /> Kembali ke Artikel
        </Link>
      </section>
    );
  }

  if (!article) {
    return (
      <section className="flex min-h-screen items-center justify-center">
        <p className="font-serif text-2xl italic text-[#737373]">Memuat…</p>
      </section>
    );
  }

  return (
    <>
      <article data-testid="article-detail" className="pt-32">
        <div className="mx-auto w-full max-w-4xl px-6">
          <FadeUp>
            <Link
              to="/artikel"
              data-testid="article-back-link"
              className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.3em] text-[#A3A3A3] transition-colors hover:text-gold"
            >
              <ArrowLeft size={14} /> Semua Artikel
            </Link>
          </FadeUp>

          <div className="mt-10">
            <Eyebrow>
              {article.kategori} — {article.tanggal} · {article.waktu_baca} baca
            </Eyebrow>
          </div>

          <h1 className="mt-8 font-serif text-4xl leading-[1.1] text-[#F5F5F5] sm:text-5xl lg:text-6xl">
            <LineReveal lines={[article.judul]} delay={0.15} />
          </h1>

          <FadeUp delay={0.3}>
            <p className="mt-6 text-[12px] font-medium uppercase tracking-[0.25em] text-[#A3A3A3]">
              Oleh {article.penulis}
            </p>
          </FadeUp>
        </div>

        <FadeUp delay={0.4} className="mx-auto mt-14 w-full max-w-6xl px-6">
          <div className="flex items-center justify-center overflow-hidden border border-white/10 bg-[#0d0d0d]">
            <img
              src={imgSrc(article.gambar)}
              alt={article.judul}
              className="max-h-[75vh] w-full object-contain"
            />
          </div>
        </FadeUp>

        <FadeUp delay={0.2}>
          <div className="article-body mx-auto w-full max-w-3xl px-6 py-16 lg:py-20">
            {article.isi.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
            <div className="mt-4 flex items-center gap-4">
              <span className="h-px w-10 bg-gold/70" />
              <span className="text-[11px] font-medium uppercase tracking-[0.3em] text-gold">
                YLBH Sumatera Timur
              </span>
            </div>

            <div className="mt-12 border-t border-white/10 pt-10">
              <ShareButtons
                url={window.location.href}
                title={article.judul}
                imageUrl={imgSrc(article.gambar)}
                testidPrefix="share"
              />
            </div>
          </div>
        </FadeUp>
      </article>

      <CtaSection
        testid="article-detail-cta"
        lines={[
          "Menghadapi persoalan serupa?",
          <span key="t" className="italic text-gold">
            Konsultasikan pada kami.
          </span>,
        ]}
      />
    </>
  );
}
